/**
 * Ported from yaqatw's src/service/cloud/dropbox.ts. Only the import paths
 * (./events, ./log — see those files' headers) and the client-id env var
 * (DROPBOX_CLIENT_ID → REACT_APP_DROPBOX_CLIENT_ID, CRA's required prefix
 * for inlining) differ from the source; all request/path-resolution logic
 * is unchanged.
 */
import Events from "./events";
import { BaseCloudProvider } from "./BaseCloudProvider";
import type { FileMetadata, PushResult, UploadResult } from "./types";
import { log } from "./log";

class DropboxClass extends BaseCloudProvider {
  public readonly tokenName = "dropboxAccessToken";
  protected readonly providerName = "Dropbox";

  private clientId = process.env.REACT_APP_DROPBOX_CLIENT_ID;
  private appFolderId: string | null = null;
  private appFolderPromise: Promise<string> | null = null;
  private folderCache: Map<string, string> = new Map();
  private folderResolutionPromises: Map<string, Promise<string>> = new Map();

  protected async performAuthenticate(): Promise<string> {
    this.assertClientId(this.clientId, "REACT_APP_DROPBOX_CLIENT_ID");
    return super.performAuthenticate();
  }

  protected getAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId!,
      redirect_uri: this.redirectUri,
      response_type: "token",
      state: Math.random().toString(36).substring(7),
    });
    return `https://www.dropbox.com/oauth2/authorize?${params}`;
  }

  /**
   * Non-interactive collaboration identity: `dropbox:<account_id>`. Uses the
   * stored token with a plain fetch (no re-auth / popup); returns null if not
   * signed in or the token is stale. `get_current_account` takes no arguments,
   * so it's a bodyless POST.
   */
  public async getIdentity(): Promise<string | null> {
    const token = localStorage.getItem(this.tokenName);
    if (!token) return null;
    try {
      const res = await fetch("https://api.dropboxapi.com/2/users/get_current_account", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.account_id ? `dropbox:${data.account_id}` : null;
    } catch {
      return null;
    }
  }

  /**
   * Ensures the application folder exists in Dropbox.
   */
  private async ensureAppFolder(): Promise<string> {
    if (this.appFolderId) return this.appFolderId;
    if (this.appFolderPromise) return this.appFolderPromise;

    this.appFolderPromise = (async () => {
      const folderPath = "/YaqatW-DATA";
      try {
        const checkResponse = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/files/get_metadata",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: folderPath }),
          }
        );
        if (checkResponse.ok) {
          const data = await checkResponse.json();
          this.appFolderId = data.path_display;
          return this.appFolderId!;
        }
        const createResponse = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/files/create_folder_v2",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: folderPath, autorename: false }),
          }
        );
        if (!createResponse.ok) {
          const error = await createResponse.json();
          if (error.error?.path?.[".tag"] === "conflict") {
            this.appFolderId = folderPath;
            return this.appFolderId!;
          }
          throw new Error(`Create failed with status ${createResponse.status}`);
        }
        const createData = await createResponse.json();
        this.appFolderId = createData.metadata.path_display;
        return this.appFolderId!;
      } catch (error) {
        console.error("Error ensuring app folder:", error);
        this.appFolderPromise = null;
        throw error;
      }
    })();

    return this.appFolderPromise;
  }

  /**
   * Resolves a file or folder path, creating folders if necessary.
   */
  private async resolvePath(
    fullPath: string,
    isFolder: boolean = false
  ): Promise<{ parentFolderId: string; fileName: string | null }> {
    if (isFolder) {
      const folderId = await this.resolveFolderPath(fullPath);
      return { parentFolderId: folderId, fileName: null };
    }
    return this.resolveFilePath(fullPath);
  }

  public async pull(itemPath: string, fileId_: string | null = null): Promise<any> {
    try {
      let fullPath = null;
      if (!fileId_) {
        const { parentFolderId, fileName: baseName } = await this.resolvePath(
          itemPath.toLowerCase(),
          false
        );
        if (!baseName || baseName === "null") return null;
        fullPath = `${parentFolderId}/${baseName}`;
      } else {
        fullPath = fileId_;
      }

      const response = await this.fetchWithAuth("https://content.dropboxapi.com/2/files/download", {
        method: "POST",
        headers: { "Dropbox-API-Arg": JSON.stringify({ path: fullPath }) },
      });
      if (!response.ok) {
        if (response.status === 409) {
          log(`[Sync] File ${itemPath} not found remotely (409), treating as new.`);
          return null;
        }
        const errorText = await response.text();
        if (errorText.includes("path/not_found")) return null;
        throw new Error(`Download failed with status ${response.status}`);
      }
      const fileContent = await response.text();
      return JSON.parse(fileContent);
    } catch (error: any) {
      if (
        error.message &&
        (error.message.includes("not found") ||
          error.message.includes("409") ||
          error.message.includes("not found remotely"))
      ) {
        console.warn(`[Sync] Suppressing known error for ${itemPath}: ${error.message}`);
        return null;
      }
      console.error(`Error pulling ${itemPath}:`, error);
      Events.emit("showError", `Sync failed: ${error.message}`);
      throw error;
    }
  }

  private async uploadToDropbox(
    fileName: string,
    data: any,
    isRetry: boolean = false
  ): Promise<PushResult | void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(
        fileName.toLocaleLowerCase(),
        false
      );
      const fullPath = `${parentFolderId}/${baseName}`;
      const fileContent = JSON.stringify(data);
      const response = await this.fetchWithAuth("https://content.dropboxapi.com/2/files/upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "Dropbox-API-Arg": JSON.stringify({ path: fullPath, mode: "overwrite" }),
        },
        body: fileContent,
      });
      if (!response.ok) {
        const errorText = await response.text();
        if ((response.status === 409 || errorText.includes("path/not_found")) && !isRetry) {
          console.warn(
            `[Dropbox] Upload path not found for ${fileName}, clearing folder cache and retrying...`
          );
          this.folderCache.clear();
          this.folderResolutionPromises.clear();
          this.appFolderId = null;
          this.appFolderPromise = null;
          return this.uploadToDropbox(fileName, data, true);
        }
        throw new Error(`Upload failed with status ${response.status}`);
      }
      const responseData = await response.json();
      log(`File ${fileName} successfully uploaded to Dropbox`);
      if (responseData?.server_modified) {
        return { modifiedTime: responseData.server_modified };
      }
    } catch (error: any) {
      console.error(`Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  public async push(fileName: string, data: any): Promise<PushResult | void> {
    return this.uploadToDropbox(fileName.toLocaleLowerCase(), data);
  }

  /**
   * Uploads any file type (binary, text, or string) to Dropbox.
   * Dropbox API only accepts application/octet-stream for binary files.
   */
  public async uploadFile(
    filePath: string,
    fileContent: Blob | string | ArrayBuffer
  ): Promise<UploadResult> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(filePath, false);
      const fullPath = `${parentFolderId}/${baseName}`;

      let bodyContent: string | ArrayBuffer | Blob;
      let contentType = "application/octet-stream";

      if (fileContent instanceof Blob) {
        bodyContent = fileContent;
      } else if (fileContent instanceof ArrayBuffer) {
        bodyContent = fileContent;
      } else if (typeof fileContent === "string") {
        bodyContent = fileContent;
        contentType = "text/plain; charset=dropbox-cors-hack";
      } else {
        bodyContent = String(fileContent);
        contentType = "text/plain; charset=dropbox-cors-hack";
      }

      const response = await this.fetchWithAuth("https://content.dropboxapi.com/2/files/upload", {
        method: "POST",
        headers: {
          "Content-Type": contentType,
          "Dropbox-API-Arg": JSON.stringify({ path: fullPath, mode: "overwrite" }),
        },
        body: bodyContent,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Dropbox upload error: ${errorText}`);
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const responseData = await response.json();
      log(`File ${filePath} successfully uploaded to Dropbox`);

      const permanentUrl = await this.getOrCreateSharedLink(fullPath);
      if (permanentUrl) return { url: permanentUrl };

      // Fall back to short-lived temporary link if shared links are disabled
      const linkResponse = await this.fetchWithAuth(
        "https://api.dropboxapi.com/2/files/get_temporary_link",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path: fullPath }),
        }
      );

      if (linkResponse.ok) {
        const linkData = await linkResponse.json();
        return { url: linkData.link };
      }
      console.warn("Could not generate temporary link, using path_display");
      return { url: responseData.path_display };
    } catch (error: any) {
      console.error(`Error uploading file ${filePath}:`, error);
      Events.emit("showError", `File upload failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Create a permanent shared link for the file at fullPath, or retrieve an
   * existing one. Returns null if shared linking is disabled by the workspace.
   * The returned URL is rewritten with ?raw=1 so it serves the raw file
   * (rather than the dropbox.com preview page).
   */
  private async getOrCreateSharedLink(fullPath: string): Promise<string | null> {
    const rewriteRaw = (u: string): string => {
      try {
        const url = new URL(u);
        url.searchParams.set("raw", "1");
        url.searchParams.delete("dl");
        return url.toString();
      } catch {
        return u;
      }
    };

    const tryCreate = async (
      visibility: "public" | "team_only"
    ): Promise<{ url?: string; existsAlready?: boolean; restricted?: boolean }> => {
      const resp = await this.fetchWithAuth(
        "https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            path: fullPath,
            settings: { requested_visibility: visibility },
          }),
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        return { url: data?.url };
      }
      let parsed: any = null;
      try {
        parsed = await resp.json();
      } catch {
        /* ignore */
      }
      const tag = parsed?.error?.[".tag"];
      if (tag === "shared_link_already_exists") return { existsAlready: true };
      if (tag === "settings_error" || tag === "email_not_verified") {
        return { restricted: true };
      }
      console.warn(`[Dropbox] create_shared_link_with_settings failed:`, parsed || resp.status);
      return { restricted: true };
    };

    try {
      let result = await tryCreate("public");
      if (!result.url && result.restricted) {
        result = await tryCreate("team_only");
      }

      if (!result.url && result.existsAlready) {
        const listResp = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/sharing/list_shared_links",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: fullPath, direct_only: true }),
          }
        );
        if (listResp.ok) {
          const listData = await listResp.json();
          const existing = listData?.links?.[0]?.url;
          if (existing) result.url = existing;
        }
      }

      return result.url ? rewriteRaw(result.url) : null;
    } catch (err) {
      console.warn(`[Dropbox] getOrCreateSharedLink failed for ${fullPath}:`, err);
      return null;
    }
  }

  public async downloadFile(filePath: string): Promise<Blob> {
    const { parentFolderId, fileName: baseName } = await this.resolvePath(
      filePath.toLowerCase(),
      false
    );
    if (!baseName || baseName === "null") {
      throw new Error(`Dropbox downloadFile: file not found: ${filePath}`);
    }
    const fullPath = `${parentFolderId}/${baseName}`;
    const response = await this.fetchWithAuth("https://content.dropboxapi.com/2/files/download", {
      method: "POST",
      headers: { "Dropbox-API-Arg": JSON.stringify({ path: fullPath }) },
    });
    if (!response.ok) {
      throw new Error(`Dropbox download failed for ${filePath}: ${response.status}`);
    }
    return response.blob();
  }

  public async delete(filePath: string): Promise<void> {
    try {
      const { parentFolderId, fileName: baseName } = await this.resolvePath(
        filePath.toLowerCase(),
        false
      );
      if (!baseName || baseName === "null") {
        console.warn(`Cannot delete: invalid file name for path ${filePath}`);
        return;
      }
      const fullPath = `${parentFolderId}/${baseName}`;
      const response = await this.fetchWithAuth("https://api.dropboxapi.com/2/files/delete_v2", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: fullPath }),
      });
      if (!response.ok) {
        if (response.status !== 409) {
          throw new Error(`Delete failed with status ${response.status}`);
        }
        console.warn(`File not found for deletion: ${filePath}`);
        return;
      }
      log(`Successfully deleted ${filePath} from Dropbox`);
    } catch (error) {
      console.error(`Error deleting ${filePath}:`, error);
      Events.emit("showError", `Delete failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pushDiff(globalEntries: any[]): Promise<void> {
    for (const entry of globalEntries) {
      await this.push(entry.path, entry.data);
      Events.emit("showStatus", { intent: "info", text: `Synced ${entry.path} to Dropbox.` });
    }
  }

  public async getMetadataAll(projectName: string): Promise<FileMetadata[]> {
    const allFiles: FileMetadata[] = [];
    try {
      const normalizedProjectName = projectName.replace(/\/$/, "");

      const traverse = async (folderPath: string) => {
        const listResponse = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/files/list_folder",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: folderPath }),
          }
        );
        if (!listResponse.ok) {
          throw new Error(`List files failed with status ${listResponse.status}`);
        }
        const listData = await listResponse.json();
        for (const file of listData.entries || []) {
          if (file[".tag"] === "folder") {
            await traverse(file.path_display);
          } else {
            const pathParts = file.path_lower.split("/").filter((p: string) => p);
            if (pathParts.length >= 3) {
              const path = pathParts.slice(2).join("/");
              allFiles.push({
                id: file.path_display,
                name: file.name,
                path: normalizedProjectName + "/" + path,
                modifiedTime: file.server_modified,
              });
            }
          }
        }
      };
      const projectFolder = await this.resolveFolderPath(normalizedProjectName);
      await traverse(projectFolder);
    } catch (error) {
      console.error(`Error getting metadata for all files under ${projectName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
    return allFiles;
  }

  public async getMetadata(fileName: string): Promise<FileMetadata[]> {
    try {
      const { parentFolderId } = await this.resolvePath(fileName, false);
      const listResponse = await this.fetchWithAuth(
        "https://api.dropboxapi.com/2/files/list_folder",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path: parentFolderId }),
        }
      );
      if (!listResponse.ok) {
        throw new Error(`List files failed with status ${listResponse.status}`);
      }
      const listData = await listResponse.json();
      return (listData.entries || [])
        .filter((f: any) => f[".tag"] !== "folder")
        .map((f: any) => ({
          id: f.path_display,
          name: f.name,
          path: f.path_lower.split("/").slice(2).join("/"),
          modifiedTime: f.server_modified,
        }));
    } catch (error) {
      console.error(`Error getting metadata for ${fileName}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pullById(fileId: string): Promise<any> {
    try {
      const response = await this.fetchWithAuth("https://content.dropboxapi.com/2/files/download", {
        method: "POST",
        headers: { "Dropbox-API-Arg": JSON.stringify({ path: fileId }) },
      });
      if (!response.ok) {
        throw new Error(`Download by ID failed with status ${response.status}`);
      }
      const fileContent = await response.text();
      return JSON.parse(fileContent);
    } catch (error) {
      console.error(`Error pulling by ID ${fileId}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  /**
   * Resolves a folder path, creating folders if necessary.
   */
  private async resolveFolderPath(fullPath: string): Promise<string> {
    let currentPath = await this.ensureAppFolder();
    if (typeof fullPath !== "string") {
      console.error("resolveFolderPath: fullPath is not a string", fullPath);
      throw new TypeError("resolveFolderPath: fullPath must be a string, got: " + typeof fullPath);
    }
    const pathParts = fullPath.split("/").filter((p) => p);
    for (const part of pathParts) {
      currentPath = await this.resolveOrCreateFolder(currentPath, part);
    }
    return currentPath;
  }

  /**
   * Resolves a file path, creating folders if necessary.
   */
  private async resolveFilePath(
    fullPath: string
  ): Promise<{ parentFolderId: string; fileName: string | null }> {
    let currentPath = await this.ensureAppFolder();
    if (typeof fullPath !== "string") {
      console.error("resolveFilePath: fullPath is not a string", fullPath);
      throw new TypeError("resolveFilePath: fullPath must be a string, got: " + typeof fullPath);
    }
    const pathParts = fullPath.split("/").filter((p) => p);
    const fileName = pathParts.length > 0 ? pathParts.pop()! : null;
    for (const part of pathParts) {
      currentPath = await this.resolveOrCreateFolder(currentPath, part);
    }
    return { parentFolderId: currentPath, fileName };
  }

  /**
   * Shared per-segment folder resolution used by both resolveFolderPath and
   * resolveFilePath. Uses the cache + in-flight promise map to coalesce
   * concurrent resolutions.
   */
  private async resolveOrCreateFolder(parentPath: string, segment: string): Promise<string> {
    const nextPath = `${parentPath}/${segment}`;
    if (this.folderCache.has(nextPath)) return this.folderCache.get(nextPath)!;
    if (this.folderResolutionPromises.has(nextPath)) {
      return this.folderResolutionPromises.get(nextPath)!;
    }
    const resolutionPromise = (async () => {
      try {
        const checkResponse = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/files/get_metadata",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: nextPath }),
          }
        );
        if (checkResponse.ok) {
          const data = await checkResponse.json();
          this.folderCache.set(nextPath, data.path_display);
          return data.path_display;
        }
        const createResponse = await this.fetchWithAuth(
          "https://api.dropboxapi.com/2/files/create_folder_v2",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: nextPath, autorename: false }),
          }
        );
        if (!createResponse.ok) {
          const error = await createResponse.json();
          if (error.error?.path?.[".tag"] === "conflict") {
            this.folderCache.set(nextPath, nextPath);
            return nextPath;
          }
          throw new Error(`Create failed with status ${createResponse.status}`);
        }
        const createData = await createResponse.json();
        const newPath = createData.metadata.path_display;
        this.folderCache.set(nextPath, newPath);
        return newPath;
      } catch (error) {
        console.error(`Error resolving path part ${segment}:`, error);
        this.folderResolutionPromises.delete(nextPath);
        throw error;
      }
    })();
    this.folderResolutionPromises.set(nextPath, resolutionPromise);
    return resolutionPromise;
  }
}

const Dropbox = new DropboxClass();
export default Dropbox;
